import { Container, Graphics, Sprite, Text } from "pixi.js"
import { stageViewport } from "../../../../canvas_layout"
import { IntroBackground } from "./intro_background"
import { GuiManager } from "../gui_manager"
import { ProjectLogo } from "./project_logo"
import { Fader } from "../gui/fader"
import { randomIntBetween } from "game-common/models"
import { BasicSprite } from "../sprites/basic_sprite"
import { TextRenderer } from "../text/text_renderer"
import { LoginCard } from "./login_card"
import { COLORS, INTRO_FONT, METRICS, SCREEN_H, SCREEN_W, drawChip, ensureIntroFont } from "./intro_theme"
import {
    enterFullscreenIfMobile,
    fullscreenSupported,
    shouldOfferHomeScreenInstall,
    toggleFullscreen,
} from "../../../../fullscreen"

type LoginCallback = (username?: string, password?: string) => void

/** Where the wordmark starts before it settles, and how long the slide takes. */
const LOGO_DROP = 120
const LOGO_SLIDE_MS = 630

/**
 * The card's middle. The wordmark settles above ~130 and the socials/fullscreen chips sit at
 * CHROME_Y, so this centres the card in what is left and lets it grow either way.
 */
const CARD_CENTER_Y = 273
const CHROME_Y = SCREEN_H - 40

/** The height of the chips on the chrome row, which the install hint is centred against. */
const CHIP_H = 34

/**
 * What an iPhone gets instead of the expand chip. Worth the words: nothing else on the page can
 * put the game edge to edge there, and the player has no reason to guess that the browser's own
 * Share menu is where the fullscreen button went.
 */
const INSTALL_HINT = "Tap Share, then Add to Home Screen, for fullscreen"

export class Intro extends Graphics {
    guiManager: GuiManager
    loggingIn: boolean
    landingContainer: Graphics
    container: Container
    socials: Graphics

    private card?: LoginCard
    private logo?: ProjectLogo
    private background?: Graphics
    private scrim?: Graphics

    constructor(guiManager: GuiManager) {
        super()
        this.guiManager = guiManager
    }

    formRemover = () => {
        this.card?.dispose()
        this.card = undefined
        this.container?.removeChildren()
        this.container?.parent?.removeChild(this.container)
        this.container = undefined
    }

    guestLandingRemover = () => {
        window.removeEventListener("samsara:canvas-resize", this.resize)
        this.formRemover()

        // Frees the wordmark's canvas rasters and stops its animation frame loop; neither is
        // reclaimed by removeChildren().
        this.logo?.dispose()
        this.logo = undefined

        this.landingContainer?.removeChildren()
        this.landingContainer?.parent?.removeChild(this.landingContainer)

        this.socials?.removeChildren()
        this.socials?.parent?.removeChild(this.socials)
    }

    /**
     * Show or hide the login form.
     *
     * Not just `container.visible`: the card's fields are real DOM `<input>`s that only follow
     * pixi's visibility from inside a render pass, so hiding the container alone leaves them --
     * and the "Pick a name, or play as a guest" placeholder -- on the page, over whatever screen
     * comes next, until the card is finally disposed. See LoginField.setDomVisible.
     */
    private setFormVisible = (visible: boolean) => {
        if (this.container) {
            this.container.visible = visible
        }
        this.card?.setDomVisible(visible)
    }

    loginSuccess = () => {
        this.loggingIn = false
        this.guestLandingRemover()
    }

    /**
     * A rejected sign-in. Returns true when the card took the message inline -- the caller then
     * skips the global screen banner, which used to land across the wordmark.
     */
    loginFailed = (): boolean => {
        this.loggingIn = false

        if (this.card) {
            this.setFormVisible(true)
            this.card.showError("That name and password don't match.")
            return true
        }

        this.setFormVisible(true)
        return false
    }

    init = (doneCallback: LoginCallback) => {
        if (this.loggingIn) {
            return
        }

        // Jost has to be resolvable before the first Text is built; a pixi Text keeps whatever face
        // it rasterized with. ensureIntroFont never rejects, so a slow CDN-less start still gets a
        // login screen, just in Verdana.
        ensureIntroFont().then(() => this.build(doneCallback))
    }

    private build = (doneCallback: LoginCallback) => {
        const landingContainer = new Graphics()
        this.landingContainer = landingContainer

        const introBackgroundGenerator = new IntroBackground()
        const background = introBackgroundGenerator.update()
        this.background = background
        // The HTML loading scene fades onto this art while the wordmark settles.
        background.alpha = 1
        background.x = 0
        landingContainer.addChild(background)

        // A touch of scrim so white text and the card's rim hold up over the busiest parts of the
        // forest without hiding the art.
        const scrim = new Graphics()
        this.scrim = scrim
        scrim.beginFill(COLORS.scrim, 0.28)
        scrim.drawRect(0, 0, SCREEN_W, SCREEN_H + 40)
        scrim.endFill()
        landingContainer.addChild(scrim)

        const butterflies = introBackgroundGenerator.bufferFlies()
        butterflies.x = 0
        landingContainer.addChild(butterflies)

        const introGenerator = new ProjectLogo()
        this.logo = introGenerator
        const intro = introGenerator.update(() => {
            introGenerator.pulse()
            this.slideLogo(intro)

            new Fader(background as Graphics, {
                direction: "in",
                minAlpha: 1.0,
                fadeAlphaDecrement: 0.005,
                releaseCallback: () => {
                    if (this.container) {
                        this.setFormVisible(true)
                        this.card?.focus()
                    }
                },
            }).update()

            this.container = this.buildLoginContainer(doneCallback)
            this.setFormVisible(false)
            this.guiManager.stage.addChild(this.container)

            this.socials = this.buildSocials(this.guiManager.stage)
            this.resize()
        })

        intro.x = Math.round((SCREEN_W - introGenerator.calcWidth()) / 2)
        intro.y = LOGO_DROP

        landingContainer.addChild(intro)
        this.guiManager.stage.addChild(landingContainer)
        this.resize()
        window.addEventListener("samsara:canvas-resize", this.resize)
        requestAnimationFrame(() => window["stopLoading"]?.())
    }

    /** Resize in place so the logo animation and entered credentials survive orientation changes. */
    private resize = () => {
        const viewport = stageViewport()
        if (this.background) {
            const art = this.background.children[0] as Sprite
            const expanded = viewport.inset > 0 || viewport.insetY > 0
            const coverHeight = Math.max(SCREEN_H + 40, viewport.height)
            const scale = expanded
                ? Math.max(viewport.width / art.texture.width, coverHeight / art.texture.height)
                : 0.3
            art.scale.set(scale)
            art.x = expanded ? (viewport.width - art.width) / 2 : 0
            art.y = expanded ? (coverHeight - art.height) / 2 : 0
            this.background.x = viewport.left
            this.background.y = viewport.top
        }
        if (this.scrim) {
            this.scrim.clear().beginFill(COLORS.scrim, 0.28)
            this.scrim.drawRect(viewport.left, viewport.top, viewport.width, Math.max(SCREEN_H + 40, viewport.height))
            this.scrim.endFill()
        }
        if (this.card) {
            // Keep the desktop card proportions; only the backdrop and edge chrome expand.
            this.card.x = Math.round((SCREEN_W - METRICS.cardWidth) / 2)
        }
        const socials = this.socials?.getChildByName("socials")
        if (socials) {
            socials.x = viewport.left + 12
            socials.y = CHROME_Y + viewport.insetY
        }
        const chrome = this.socials?.getChildByName("rightChrome") as Container | undefined
        if (chrome) {
            chrome.x = viewport.right - 12 - chrome.width
            chrome.y = CHROME_Y + viewport.insetY + (CHIP_H - chrome.height) / 2
        }
    }

    /**
     * Ease the wordmark up to its resting place (the old version stepped 1px on a 1ms timer).
     *
     * Expo rather than cubic: it covers most of the distance early and then creeps the last few
     * pixels, which reads as the block settling into place instead of coasting to a stop. No
     * overshoot here -- the wordmark rests at y=0, the top of the screen, so there is nothing above
     * it to spring into. The spring lives in the individual letters, where there is room for it.
     *
     * LOGO_SLIDE_MS is the one dial for how gradual this feels; it is the whole travel time.
     */
    private slideLogo = (intro: Container) => {
        const started = Date.now()
        const tick = () => {
            const t = Math.min(1, (Date.now() - started) / LOGO_SLIDE_MS)
            const eased = t >= 1 ? 1 : 1 - Math.pow(2, -10 * t)
            intro.y = Math.round(LOGO_DROP * (1 - eased))
            if (t < 1) {
                requestAnimationFrame(tick)
            }
        }
        requestAnimationFrame(tick)
    }

    private buildLoginContainer = (doneCallback: LoginCallback): Container => {
        const container = new Container()

        const guestName = "Guest " + randomIntBetween(1, 10000)

        const card = new LoginCard({
            guestName,
            centerY: CARD_CENTER_Y,
            onPlay: (playerName: string) => {
                // A phone can only expand from inside a gesture, and this is the first one that
                // means "I am playing" -- the taps before it may be aimed at the name field, and
                // resizing the page out from under an opening soft keyboard is its own mess.
                enterFullscreenIfMobile()
                this.setFormVisible(false)
                setTimeout(() => doneCallback(playerName), 250)
            },
            onLogin: (username: string, password: string) => {
                enterFullscreenIfMobile()
                this.loggingIn = true
                this.setFormVisible(false)
                doneCallback(username, password)
            },
        })

        // y is the card's own business -- it re-centres itself on CARD_CENTER_Y after each layout.
        card.x = Math.round((SCREEN_W - METRICS.cardWidth) / 2)

        container.addChild(card)
        this.card = card

        return container
    }

    buildSocials = (parent?: Container): Graphics => {
        const allContainer = new Graphics()
        if (parent) {
            parent.addChild(allContainer)
        }

        const socialContainer = new Graphics()
        socialContainer.name = "socials"
        socialContainer.x = 12
        socialContainer.y = CHROME_Y

        const socialMedia = this.guiManager.socialMedia()
        // socialMedia() positions its own row for the in-game HUD; the intro parks it in a chip.
        socialMedia.y = 0
        socialMedia.x = 0

        drawChip(socialContainer, socialMedia.width + 20, 34)
        socialMedia.x = 10
        socialMedia.y = Math.round((34 - socialMedia.height) / 2)
        socialContainer.addChild(socialMedia)
        allContainer.addChild(socialContainer)

        // No chip where the platform has no fullscreen to give: iPhone, and an embed framed
        // without allowfullscreen. Better an absent control than one that visibly does nothing --
        // but a phone in that position still has Add to Home Screen, so say so rather than leave
        // the corner empty.
        if (!fullscreenSupported()) {
            if (shouldOfferHomeScreenInstall()) {
                allContainer.addChild(this.buildInstallHint())
            }
            return allContainer
        }

        const fullscreenChip = new Graphics()
        fullscreenChip.name = "rightChrome"
        drawChip(fullscreenChip, CHIP_H, CHIP_H)
        fullscreenChip.x = SCREEN_W - CHIP_H - 12
        fullscreenChip.y = CHROME_Y

        const expandFullScreen = new BasicSprite({ name: "fullscreen.png", scale: 0.55 })
        expandFullScreen.x = 17
        expandFullScreen.y = 17
        fullscreenChip.addChild(expandFullScreen)

        fullscreenChip.interactive = true
        fullscreenChip.cursor = "pointer"
        fullscreenChip.on("click", toggleFullscreen)
        fullscreenChip.on("tap", toggleFullscreen)

        allContainer.addChild(fullscreenChip)

        return allContainer
    }

    /**
     * Plain muted type rather than a chip: a chip reads as a button, and there is nothing here to
     * press -- the control it names belongs to the browser, not to us.
     */
    private buildInstallHint = (): Text => {
        const hint = new TextRenderer()
            .fontFamily(INTRO_FONT)
            .role("caption").bold(false)
            .color(COLORS.muted)
            .render(INSTALL_HINT) as Text

        hint.x = Math.round(SCREEN_W - 12 - hint.width)
        hint.name = "rightChrome"
        hint.y = Math.round(CHROME_Y + (CHIP_H - hint.height) / 2)
        return hint
    }
}
